/**
 * Contrato de mensajes entre los dos marcos: el de la web del cliente (loader) y el del panel
 * (iframe).
 *
 * La cabecera `Origin` de una petición es siempre la del documento que la hace, así que una
 * llamada desde el panel llevaría nuestro origen y la lista de dominios permitidos no significaría
 * nada: quien pide la sesión es el loader. De ahí el reparto: el loader posee la identidad del
 * visitante y la credencial, y le entrega al panel lo justo para conversar. La identidad de vida
 * larga **no entra en el iframe**.
 */

import type { SessionConversation, SessionLimits, SessionPresentation } from './api';

/** Remitente de los mensajes que salen del panel. */
export const PANEL_SOURCE = 'condato-atlas-widget/panel';

/** Remitente de los mensajes que salen del loader. */
export const HOST_SOURCE = 'condato-atlas-widget/host';

/**
 * Versión del contrato. Va en cada mensaje porque los dos marcos se despliegan juntos pero no se
 * cargan juntos: el loader se cachea hasta cinco minutos, así que un loader viejo puede estar
 * hablando con un panel nuevo.
 */
export const PROTOCOL_VERSION = 2;

/**
 * Lo que el panel necesita para conversar: la respuesta de la sesión **sin la identidad del
 * visitante**, que se queda en el marco del cliente.
 */
export interface PanelBootstrap {
  publicId: string;

  /** Credencial de vida corta que autoriza los turnos. */
  sessionToken: string;

  /** Base del streaming. Vacía significa «la del API». */
  streamBase?: string | null;

  presentation: SessionPresentation;
  limits: SessionLimits;

  /** Conversación que se reanuda, si el visitante ya tenía una. */
  conversation?: SessionConversation | null;
}

/** Mensajes que el panel envía al marco que lo contiene. */
export type PanelMessage =
  /**
   * El panel ha arrancado y espera su arranque. Sincroniza las dos cosas que ocurren en paralelo al
   * abrir —cargar el iframe y pedir la sesión—, de modo que la espera sea la más lenta y no la suma.
   */
  | { source: typeof PANEL_SOURCE; version: number; type: 'ready' }

  /**
   * El visitante ha cerrado el panel desde dentro. Sube al padre porque quien puede ocultar el
   * iframe y devolver el foco al lanzador es el marco de fuera.
   */
  | { source: typeof PANEL_SOURCE; version: number; type: 'close' }

  /**
   * La credencial de sesión ha caducado y el panel pide otra. Solo el loader puede emitirla.
   *
   * Es opcional: un loader anterior no la entiende y no contesta, así que el panel la espera con un
   * plazo y cae en el aviso de «vuelve a abrir el chat».
   */
  | { source: typeof PANEL_SOURCE; version: number; type: 'renew' };

/** Mensajes que el loader envía al panel. */
export type HostMessage =
  /** Todo lo necesario para pintarse y conversar. */
  | { source: typeof HOST_SOURCE; version: number; type: 'bootstrap'; bootstrap: PanelBootstrap }

  /** La sesión no se ha podido abrir. El loader es quien habla con el API y se entera del rechazo. */
  | { source: typeof HOST_SOURCE; version: number; type: 'unavailable'; reason: string }

  /**
   * El panel se ha hecho visible. Se oculta y se muestra sin recargarse, así que necesita saber
   * cuándo vuelve a estar a la vista para devolver el foco al campo de texto.
   */
  | { source: typeof HOST_SOURCE; version: number; type: 'shown' }

  /**
   * Credencial de sesión recién emitida, en respuesta a un `renew`.
   *
   * Lleva solo la credencial y no el arranque completo: la presentación y el hilo ya están en
   * pantalla, y volver a entregarlos repintaría la conversación entera debajo de sí misma.
   */
  | {
      source: typeof HOST_SOURCE;
      version: number;
      type: 'session';
      sessionToken: string;

      /** Base del streaming. Vacía significa «la del API». Puede cambiar entre sesiones. */
      streamBase?: string | null;
    };

/**
 * Comprueba que lo recibido es un mensaje del panel. El origen se valida aparte, en quien escucha:
 * es la comprobación que de verdad autoriza y no debe darse por hecha aquí.
 */
export function isPanelMessage(data: unknown): data is PanelMessage {
  return hasShape(data, PANEL_SOURCE);
}

/** Comprueba que lo recibido es un mensaje del loader. */
export function isHostMessage(data: unknown): data is HostMessage {
  return hasShape(data, HOST_SOURCE);
}

function hasShape(data: unknown, source: string): boolean {
  if (typeof data !== 'object' || data === null) return false;

  const message = data as Record<string, unknown>;

  return message['source'] === source
    && message['version'] === PROTOCOL_VERSION
    && typeof message['type'] === 'string';
}
